import React, { useContext, useEffect, useState, useRef, useMemo } from 'react'

import StoreContext from './StoreContext'


const getIn = (obj, arrPath) => {
  const [ key, ...restPath ] = arrPath

  if (!restPath.length) {
    return obj[key]
  }

  return getIn(obj[key], restPath)
}

const strPathToArr = (str) => str.split('.').filter((v) => v)

const lookup = (state, propMap) => {
  if (typeof propMap === 'function') {
    return propMap(state)
  }

  if (typeof propMap === 'string') {
    return getIn(state, strPathToArr(propMap))
  }

  throw new Error(`Unknown lookup value: ${propMap}`)
}

const mapStateToProps = (state, propsMap) => {
  if (typeof propsMap === 'function') {
    return propsMap(state)
  }

  return Object.keys(propsMap).reduce((res, propName) => ({
    ...res,
    [propName]: lookup(state, propsMap[propName]),
  }), {})
}


/**
 *
 * @param {Object} initialState
 * @param {Function|string} [propsMap]
 * @returns {function(*=)}
 */
const createListener = (initialState, propsMap) => {
  let prevValue = propsMap ? lookup(initialState, propsMap) : initialState

  return (newState) => {
    const newValue  = propsMap ? lookup(newState, propsMap) : newState
    const isEqual   = JSON.stringify(prevValue) === JSON.stringify(newValue) // TODO replace this with memo

    prevValue = newValue

    return !isEqual
  }
}

/**
 *
 * @param store
 * @param {Object} propsMap
 * @returns {{root: Array}}
 */
const createListeners = (store, propsMap) => {
  const initialState = store.getState()
  const listeners = {}

  Object.keys(propsMap).forEach((propName) => {
    const propMap = propsMap[propName]

    // useConnect({ count: (state) => state.models.counter.count })
    if (typeof propMap === 'function') {
      if (!listeners.root) {
        listeners.root = []
      }

      listeners.root.push(createListener(initialState, propMap))
    }
    // useConnect({ count: 'models.counter.count' })
    else if (typeof propMap === 'string') {
      const [ rootName, reducerName, ...path ] = strPathToArr(propMap)

      const listenerKey       = `${rootName}.${reducerName}` // models.counter
      const reducerState      = initialState[rootName][reducerName]
      const listenerPropsMap  = path.length ? path.join('.') : null
      const listener          = createListener(reducerState, listenerPropsMap)

      if (!listeners[listenerKey]) {
        listeners[listenerKey] = []
      }

      listeners[listenerKey].push(listener)
    }
    else {
      throw new Error(`Invalid prop "${propName}" of type "${typeof propMap}" supplied to "useConnect", expected "string" or "function".`)
    }
  })

  return listeners
}

/**
 *
 * @param {Object|Function} propsMap
 * @param {Array} deps
 * @returns {*}
 */
const useConnect = (propsMap, deps = []) => {
  const { store, storeEvents } = useContext(StoreContext)

  const ref = useRef()
  const [ renderIndex, setRenderIndex ] = useState(0)

  // subscribe to redux store on component mount
  if (!ref.current) {
    ref.current = {
      handlers: [],
    }

    let listeners = {}

    if (typeof propsMap === 'function') {
      listeners.root = [ createListener(store.getState(), propsMap) ]
    }
    else {
      listeners = createListeners(store, propsMap)
    }

    Object.keys(listeners).forEach((listenerKey) => {
      const handler = (newState) => {
        const isChanged = listeners[listenerKey].some((listener) => listener(newState))

        if (isChanged) {
          setRenderIndex((state) => state + 1)
        }
      }

      ref.current.handlers.push({ key: listenerKey, handler })
      storeEvents.subscribe(listenerKey, handler)
    })
  }

  // unsubscribe from redux store on component unmount
  useEffect(() => () => {
    ref.current.handlers.forEach(({ key, handler }) => {
      storeEvents.unsubscribe(key, handler)
    })

    ref.current = null
  }, [])

  return useMemo(() => mapStateToProps(store.getState(), propsMap), [ renderIndex, ...deps ])
}


export default useConnect

export {
  createListeners,
}
